import { type RefObject, useEffect, useRef } from 'react';
import { useMobileDetection } from './useMobileDetection';
import { useVisualViewport } from './useVisualViewport';

interface UseScrollOnFocusOptions {
  scrollContainerRef: RefObject<HTMLElement>;
  enabled?: boolean;
  offset?: number;
}

export function useScrollOnFocus({
  scrollContainerRef,
  enabled = true,
  offset = 20,
}: UseScrollOnFocusOptions) {
  const isMobile = useMobileDetection();
  const { isKeyboardVisible, height: viewportHeight } = useVisualViewport();
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const scrollElementIntoView = (element: HTMLElement, delay = 0) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      const targetRect = element.getBoundingClientRect();
      const containerRect = scrollContainer.getBoundingClientRect();
      const visualViewportHeight =
        window.visualViewport?.height || window.innerHeight;

      const relativeTop =
        targetRect.top - containerRect.top + scrollContainer.scrollTop;
      const elementBottom = targetRect.bottom;

      const isHiddenByKeyboard = elementBottom > visualViewportHeight;
      const isPartiallyHidden = elementBottom > visualViewportHeight - offset;
      const isBelowContainerView = targetRect.bottom > containerRect.bottom;

      if (isHiddenByKeyboard || isPartiallyHidden || isBelowContainerView) {
        const visibleSpace =
          visualViewportHeight - containerRect.top - offset * 2;
        const idealPosition =
          containerRect.top + Math.min(visibleSpace * 0.3, 100);
        const targetScroll = relativeTop - idealPosition + containerRect.top;

        scrollContainer.scrollTo({
          top: Math.max(0, targetScroll),
        });
      }
    }, delay);
  };

  useEffect(() => {
    if (!enabled || !isMobile) {
      return;
    }

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [enabled, isMobile]);

  useEffect(() => {
    if (!enabled || !isMobile || !scrollContainerRef.current) {
      return;
    }

    const scrollContainer = scrollContainerRef.current;

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;

      if (
        !target ||
        !(
          target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement
        )
      ) {
        return;
      }

      lastFocusedElementRef.current = target;
      scrollElementIntoView(target, 350);
    };

    scrollContainer.addEventListener('focusin', handleFocusIn, true);

    return () => {
      scrollContainer.removeEventListener('focusin', handleFocusIn, true);
    };
  }, [isMobile, enabled, scrollContainerRef, offset]);

  useEffect(() => {
    if (!enabled || !isMobile || !isKeyboardVisible) {
      return;
    }

    if (
      lastFocusedElementRef.current &&
      document.activeElement === lastFocusedElementRef.current
    ) {
      scrollElementIntoView(lastFocusedElementRef.current, 300);
    }
  }, [
    isKeyboardVisible,
    viewportHeight,
    enabled,
    isMobile,
    scrollContainerRef,
    offset,
  ]);
}
